import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { StoreService } from '../../store.service';

export interface VisitorSummaryMetrics {
  totalVisitors: number;
  totalSessions: number;
  avgSessions: string;
  avgDurationMs: number;
  returningRate: string;
}

@Component({
  selector: 'app-replay',
  templateUrl: './replay.component.html',
  styleUrls: ['./replay.component.scss'],
})
export class ReplayComponent implements OnInit {
  bearer: string;
  storeId: string;
  visitors: any[] = [];
  isLoading = true;
  sessionReplayEnabled = true;

  currentPage = 1;
  pageSize = 20;
  totalRecord = 0;
  totalPage = 0;
  start = 0;
  end = 0;
  private scrollToVisitorId: string | null = null;

  // Search & client-side filtering
  searchQuery = '';
  activeBehaviorFilter = 'all'; // 'all' | 'returning' | 'frequent' | 'single'
  showInfoBanner = true;
  copiedId: string | null = null;

  DATE_FILTERS = [
    { display: 'Today', value: 'today' },
    { display: 'Yesterday', value: 'yesterday' },
    { display: 'Last 14 Days', value: 'last14' },
  ];
  selectedDateFilter = this.DATE_FILTERS[2]; // Default: Last 14 Days
  startDate: string;
  endDate: string;

  BEHAVIOR_FILTERS = [
    { label: 'All Visitors', value: 'all', tooltip: 'Show all recorded visitors' },
    { label: 'Returning (> 1)', value: 'returning', tooltip: 'Visitors who returned for 2 or more sessions' },
    { label: 'Frequent (≥ 5)', value: 'frequent', tooltip: 'High-frequency visitors with 5 or more recorded sessions' },
    { label: 'Single Visit (1)', value: 'single', tooltip: 'First-time visitors with only 1 recorded session' },
  ];

  constructor(
    private svc: StoreService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.bearer = localStorage.getItem('bearer');
    this.storeId = localStorage.getItem('store_id');
    const pageParam = Number(this.route.snapshot.queryParamMap.get('vp'));
    if (pageParam > 0) this.currentPage = pageParam;
    this.scrollToVisitorId = this.route.snapshot.queryParamMap.get('visitor');

    if (localStorage.getItem('dismiss_replay_notice') === 'true') {
      this.showInfoBanner = false;
    }

    this.applyDateFilter();
    this.checkSessionReplaySetting();
    this.loadVisitors();
  }

  checkSessionReplaySetting(): void {
    const storeId = this.storeId || localStorage.getItem('store_id');
    if (!storeId) {
      this.sessionReplayEnabled = true;
      return;
    }
    this.svc.getStoreConfig(this.bearer, storeId, 'session_replay').subscribe(
      (res: any) => {
        const data = res?.data;
        if (!data || data?.error === null) {
          this.sessionReplayEnabled = true;
          return;
        }
        const rawVal = data?.value !== undefined ? data.value : (data?.session_replay !== undefined ? data.session_replay : data);
        if (rawVal === undefined || rawVal === null) {
          this.sessionReplayEnabled = true;
          return;
        }
        const strVal = String(rawVal).trim().toLowerCase();
        this.sessionReplayEnabled = strVal !== 'false' && strVal !== '0' && strVal !== 'disabled' && strVal !== 'off';
      },
      () => { this.sessionReplayEnabled = true; }
    );
  }

  applyDateFilter(): void {
    const today = moment();
    if (this.selectedDateFilter.value === 'today') {
      this.startDate = today.format('YYYY-MM-DD');
      this.endDate   = today.format('YYYY-MM-DD');
    } else if (this.selectedDateFilter.value === 'yesterday') {
      const yesterday = today.clone().subtract(1, 'days');
      this.startDate = yesterday.format('YYYY-MM-DD');
      this.endDate   = yesterday.format('YYYY-MM-DD');
    } else {
      this.startDate = today.clone().subtract(13, 'days').format('YYYY-MM-DD');
      this.endDate   = today.format('YYYY-MM-DD');
    }
  }

  selectDateFilter(filter: { display: string; value: string }): void {
    this.selectedDateFilter = filter;
    this.currentPage = 1;
    this.applyDateFilter();
    this.loadVisitors();
  }

  setBehaviorFilter(filterValue: string): void {
    this.activeBehaviorFilter = filterValue;
  }

  loadVisitors(): void {
    this.isLoading = true;
    this.svc.getReplayVisitors(this.bearer, this.storeId, this.currentPage, this.pageSize, this.startDate, this.endDate).subscribe(
      (res) => {
        this.visitors = res?.data?.data ?? [];
        const pagination = res?.data?.pagination;
        if (pagination) {
          this.currentPage = pagination.page;
          this.totalRecord = pagination.total_record;
          this.totalPage   = pagination.total_page;
        }
        this.start = (this.currentPage - 1) * this.pageSize;
        this.end   = this.start + this.visitors.length;
        this.isLoading = false;
        if (this.scrollToVisitorId) {
          const targetId = this.scrollToVisitorId;
          this.scrollToVisitorId = null;
          setTimeout(() => {
            const el = document.getElementById('visitor-' + targetId);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
      },
      () => { this.isLoading = false; }
    );
  }

  get filteredVisitors(): any[] {
    let list = this.visitors;

    // Search query filter
    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      list = list.filter(v => v.visitor_id && v.visitor_id.toLowerCase().includes(q));
    }

    // Behavior filter
    if (this.activeBehaviorFilter === 'returning') {
      list = list.filter(v => (v.session_count || 0) > 1);
    } else if (this.activeBehaviorFilter === 'frequent') {
      list = list.filter(v => (v.session_count || 0) >= 5);
    } else if (this.activeBehaviorFilter === 'single') {
      list = list.filter(v => (v.session_count || 0) <= 1);
    }

    return list;
  }

  get displayedMetrics(): VisitorSummaryMetrics {
    const list = this.filteredVisitors;
    if (!list.length) {
      return {
        totalVisitors: 0,
        totalSessions: 0,
        avgSessions: '0.0',
        avgDurationMs: 0,
        returningRate: '0%',
      };
    }

    let totalSessions = 0;
    let returningCount = 0;

    list.forEach(v => {
      const count = Number(v.session_count) || 1;
      totalSessions += count;
      if (count > 1) returningCount++;
    });

    const avgSessions = (totalSessions / list.length).toFixed(1);
    const returningRate = Math.round((returningCount / list.length) * 100) + '%';

    return {
      totalVisitors: list.length,
      totalSessions,
      avgSessions,
      avgDurationMs: 0,
      returningRate,
    };
  }

  onPageChange(): void {
    this.loadVisitors();
  }

  viewSessions(visitorId: string): void {
    this.router.navigate(['/dashboard/replay', visitorId], {
      queryParams: { vp: this.currentPage },
    });
  }

  copyText(text: string, id: string, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      this.copiedId = id;
      setTimeout(() => {
        if (this.copiedId === id) this.copiedId = null;
      }, 2000);
    });
  }

  dismissNotice(): void {
    this.showInfoBanner = false;
    try {
      localStorage.setItem('dismiss_replay_notice', 'true');
    } catch {}
  }

  shortId(id: string): string {
    return id ? id.split('-')[0] : '-';
  }

  getVisitorSpan(firstSeen: string, lastSeen: string): string {
    if (!firstSeen || !lastSeen) return 'Single visit';
    const d1 = moment(firstSeen);
    const d2 = moment(lastSeen);
    const days = d2.diff(d1, 'days');
    if (days > 1) return `Active across ${days} days`;
    if (days === 1) return 'Active across 2 days';
    const hours = d2.diff(d1, 'hours');
    if (hours > 0) return `Active over ${hours}h`;
    return 'Same day activity';
  }

  getVisitorEngagement(v: any): { label: string; badgeClass: string; icon: string; tooltip: string } {
    const count = Number(v.session_count) || 1;
    if (count >= 20) {
      return {
        label: 'Frequent Visitor',
        badgeClass: 'bg-purple-subtle text-purple',
        icon: 'fi-rr-flame',
        tooltip: `${count} recorded sessions: Highly active customer with frequent recurring visits across multiple days.`
      };
    }
    if (count >= 5) {
      return {
        label: 'Frequent (≥ 5)',
        badgeClass: 'bg-purple-subtle text-purple',
        icon: 'fi-rr-flame',
        tooltip: `${count} recorded sessions: Frequent visitor who visits your store regularly.`
      };
    }
    if (count > 1) {
      return {
        label: 'Returning (2-4)',
        badgeClass: 'bg-info-subtle text-info',
        icon: 'fi-rr-refresh',
        tooltip: `${count} recorded sessions: Customer has visited your store multiple times.`
      };
    }
    return {
      label: 'Single Visit',
      badgeClass: 'bg-light text-muted',
      icon: 'fi-rr-user',
      tooltip: '1 recorded session: First-time or single recorded visit.'
    };
  }

  getActivityDots(v: any): number[] {
    const sessionCount = Number(v.session_count) || 1;
    let score = 1;
    if (sessionCount >= 20) score = 5;
    else if (sessionCount >= 5) score = 4;
    else if (sessionCount >= 2) score = 3;
    else score = 1;
    return Array.from({ length: 5 }, (_, i) => (i < score ? 1 : 0));
  }
}
